import type { HttpResponse } from '@angular/common/http';
import { inject, Service } from '@angular/core';
import { ActionHelper } from '@onyx/angular';
import { castArray, cloneDeep, isArray, set } from 'lodash';
import type { Observable } from 'rxjs';
import { forkJoin, map, of, switchMap, tap, zip } from 'rxjs';
import { environment } from '../../../config/environment';
import { AUTH_INTERCEPTOR_NAMESPACE_HEADER } from '../../auth/common/constants/auth-interceptor-namespace-header';
import type { BatchFileUpload } from '../interfaces/utilities/batch-file-upload';
import type { BatchFileUploadResponse } from '../interfaces/utilities/batch-file-upload-response';
import { ApiService } from './api.service';

@Service()
export class StorageService extends ApiService {
  private readonly actionHelper = inject(ActionHelper);

  public getFile(uuid: string, fileName?: string): Observable<File> {
    return this.getAsset(`${environment.apiUrl}/storage/${uuid}`, fileName);
  }

  public previewFile(uuid: string, fileName?: string) {
    return this.getFile(uuid, fileName).pipe(
      tap((file) => this.showPreview(file)),
    );
  }

  public downloadFile(uuid: string, fileName?: string) {
    return this.getFile(uuid, fileName).pipe(
      tap((file) => this.actionHelper.downloadFile(file)),
    );
  }

  public getAsset(url: string, fileName?: string): Observable<File> {
    return (
      this.http.get(url, {
        observe: 'response' as any,
        responseType: 'blob' as any,
      }) as unknown as Observable<HttpResponse<Blob>>
    ).pipe(
      map((response) => {
        if (!response.body) throw new Error('File not found');

        const blob = response.body;
        fileName ??= response.headers
          .get('content-disposition')
          ?.split('filename=')[1]
          .split(';')[0];

        return new File([blob], fileName || '', { type: blob.type });
      }),
    );
  }

  public previewAsset(url: string, fileName?: string): void {
    this.getAsset(url, fileName).subscribe((file) => this.showPreview(file));
  }

  public downloadAsset(url: string, fileName?: string): void {
    this.getAsset(url, fileName).subscribe((file) =>
      this.actionHelper.downloadFile(file),
    );
  }

  public showPreview(target: Blob | MediaSource): void {
    ActionHelper.openLink(URL.createObjectURL(target));
  }

  public uploadFile(file: File, namespace?: string): Observable<string> {
    const uploadFile = new File([file], file.name || `file.${file.type}`, {
      type: file.type,
      lastModified: file.lastModified,
    });

    const data = new FormData();
    data.set('file', uploadFile);

    return this.post('/storage', data, {
      headers: {
        ...(namespace && { [AUTH_INTERCEPTOR_NAMESPACE_HEADER]: namespace }),
      },
    }).pipe(map((response: any) => response.storageUuid));
  }

  public uploadBatch(
    data: BatchFileUpload[],
  ): Observable<BatchFileUploadResponse[]> {
    return of(data).pipe(
      map((data) =>
        data.filter(
          (upload) =>
            !!upload.files && (!isArray(upload.files) || upload.files.length),
        ),
      ),
      map((data) =>
        data.flatMap((upload) => {
          const files = castArray(upload.files);
          return files.map((file) =>
            zip(
              of(upload),
              file instanceof File ? this.uploadFile(file) : of(file),
            ),
          );
        }),
      ),
      switchMap((requests$) =>
        requests$.length ? forkJoin(requests$) : of([]),
      ),
      map((responses) =>
        responses.reduce((result, [upload, uuid]) => {
          const item = result.find((r) => r.path === upload.path);
          if (!uuid) {
            throw new Error('Invalid batch upload response');
          } else if (!item) {
            result.push({
              path: upload.path,
              uuids: isArray(upload.files) ? [uuid] : uuid,
            });
          } else if (isArray(item.uuids)) {
            item.uuids = [...item.uuids, uuid];
          }
          return result;
        }, [] as BatchFileUploadResponse[]),
      ),
    );
  }

  public mergeBatch<T extends object>(
    object: T,
    data: BatchFileUploadResponse[],
  ): T {
    const copy = cloneDeep(object);
    for (const item of data) {
      set(copy, item.path, item.uuids);
    }
    return copy;
  }
}
